import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Col, Form, Select as SelectAntd } from 'antd';
import { TextArea, Popup, Select, DatePicker, Button, BUTTON_TYPES } from '_atoms';
import { Typography } from '_atoms/Typography';
import { REQUIRED_RULE } from '_organisms/InterviewFeedback/constants';
import { dateFormatter } from 'helpers/dateFormatter';
import { BUTTON_TEXT } from 'constants/text';
import { JOB_SUBMIT_OPTIONS } from 'constants/job';
import { getOpenings } from 'actions';
import { getOpeningCandidate } from 'store/selectors/jobSelectors';
import { useAppDispatch } from 'store';
import type { Opening } from 'store/reducers/jobReducer';
import type { Moment } from 'moment';
import type { SelectOption } from '_atoms/Select';
import { useRejectionOptions } from '../PopupSubmitDecision/utils';
import type { DecisionOption } from '../PopupSubmitDecision/utils';
import { DECISION_OPTIONS, INITIAL_VALUES } from './constants';
import './style.scss';

interface PopupSubmitOfferDecisionUserData {
  first_name_latin?: string;
  last_name_latin?: string;
  full_name_latin?: string;
  first_name_local?: string;
  last_name_local?: string;
  full_name_local?: string;
  speciality?: string;
  offer_decision?: { entry_date?: string | null } | null;
}

interface LocationOption extends SelectOption {
  label: string;
  value: string;
}

interface OfferDecisionFormValues {
  decision?: string;
  opening?: { value: string; label: string } | string;
  comment?: string;
  office?: string;
  date?: Moment;
  rejection?: string;
}

type OfferDecisionPayload =
  | { opening?: string; entry_date?: string; comment?: string; positive: boolean; office?: string }
  | { comment?: string; reason?: string; positive: boolean; rejected_by: undefined };

interface PopupSubmitOfferDecisionProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: OfferDecisionPayload) => void;
  userData?: PopupSubmitOfferDecisionUserData;
  isOnboarding?: boolean;
  candidateLink?: string;
  jobId?: string;
}

export const PopupSubmitOfferDecision: React.FC<PopupSubmitOfferDecisionProps> = ({
  open,
  onClose,
  onSubmit,
  userData = {},
  isOnboarding,
  candidateLink,
  ...props
}) => {
  const {
    first_name_latin,
    last_name_latin,
    full_name_latin,
    first_name_local,
    last_name_local,
    full_name_local,
    speciality,
  } = userData;

  const { id: jobId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const opening = useSelector(getOpeningCandidate);
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const decision = Form.useWatch('decision', form);
  const rejectionReasons = useRejectionOptions(isOnboarding ? 6 : 5, { value: decision } as DecisionOption);

  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [openings, setOpenings] = useState<Opening[]>([]);

  const onSubmitClick = ({
    decision: formDecision,
    opening: formOpening,
    comment,
    office,
    ...values
  }: OfferDecisionFormValues) => {
    const positive = formDecision === JOB_SUBMIT_OPTIONS.APPROVE;
    const data = positive
      ? {
          opening: typeof formOpening === 'string' ? formOpening : formOpening?.value,
          entry_date: values.date?.format('YYYY-MM-DD'),
          comment,
          positive,
          office,
        }
      : {
          comment,
          reason: values.rejection,
          positive,
          rejected_by: undefined,
        };
    onSubmit(data);
  };

  const onChangeOpening = ({ offices }: Opening, isSetFirstLocation = true) => {
    setLocationOptions((offices || []).map(i => ({ label: i.name, value: i.uuid })));
    if (isSetFirstLocation) form.setFieldValue('office', (offices || []).length < 2 ? offices?.[0].uuid : undefined);
  };

  useEffect(() => {
    if (open) {
      dispatch(getOpenings(jobId || props.jobId, { status: 'Open' }))
        .then(ops => {
          setOpenings(ops || []);
        })
        .catch(() => {
          setOpenings([]);
        });
      if (opening) {
        const location = opening.offices?.find(l => l.uuid === opening.hired_location);

        form.setFieldsValue({
          date: userData.offer_decision?.entry_date
            ? moment(userData.offer_decision.entry_date, 'YYYY-MM-DD')
            : moment().add(7, 'days'),
          opening: {
            value: opening.uuid,
            label: `${opening.level} - ${opening.team_lead.full_name}`,
          },
          office: location?.uuid,
        });
        onChangeOpening(opening, false);
      }
    } else {
      form.resetFields();
      setLocationOptions([]);
    }
  }, [open, opening, userData]);

  return (
    <Popup
      open={open}
      className="popup-submit-offer-decision mobile-full-size"
      title={t('translation:popup_submit_offer_decision.title')}
      cancelButtonProps={{
        onClick: onClose,
        children: BUTTON_TEXT.CANCEL,
      }}
      okButtonProps={{
        children: t('translation:popup_submit_offer_decision.button_submit_decision'),
        form: 'offer-decision',
        htmlType: 'submit',
      }}
    >
      <div className="user-info">
        <Typography.Text className="user-info__title">
          {full_name_latin || `${first_name_latin} ${last_name_latin}`}
        </Typography.Text>
        <Typography.Caption className="user-info__subtitle">
          {full_name_local || `${first_name_local} ${last_name_local}`}
        </Typography.Caption>
        <Typography.Caption className="user-info__subtitle">{speciality}</Typography.Caption>
        {candidateLink && (
          <a target="_blank" href={candidateLink} rel="noreferrer">
            <Button type={BUTTON_TYPES.TEXT}>
              {t('translation:popup_submit_offer_decision.button_view_candidate')}
            </Button>
          </a>
        )}
      </div>
      <Form name="offer-decision" form={form} initialValues={INITIAL_VALUES} onFinish={onSubmitClick}>
        <Select
          label={t('translation:popup_submit_offer_decision.label_decision')}
          placeholder={t('translation:popup_submit_offer_decision.ph_select_decision')}
          options={DECISION_OPTIONS}
          itemProps={{ name: 'decision', rules: [REQUIRED_RULE] }}
          onChange={() => {
            form.setFieldValue('rejection', rejectionReasons[0].value);
          }}
        />
        <Form.Item noStyle shouldUpdate={(p, c) => p.decision !== c.decision}>
          {({ getFieldValue }) =>
            getFieldValue('decision') === JOB_SUBMIT_OPTIONS.APPROVE && (
              <>
                <Col span={12}>
                  <DatePicker
                    label={t('translation:popup_submit_offer_decision.label_entry_date')}
                    disabled={isOnboarding}
                    itemProps={{ name: 'date', rules: [REQUIRED_RULE] }}
                  />
                </Col>
                <Select
                  label={t('translation:popup_submit_offer_decision.label_opening')}
                  placeholder={t('translation:popup_submit_offer_decision.ph_select_opening')}
                  disabled={isOnboarding}
                  itemProps={{ name: 'opening', rules: [REQUIRED_RULE] }}
                  onChange={onChangeOpening}
                >
                  {openings.map(i => (
                    <SelectAntd.Option
                      key={i.uuid}
                      value={i.uuid}
                      label={`${i.level} - ${i.team_lead.full_name}`}
                      offices={i.offices}
                    >
                      <div className="opening-select-option">
                        <Typography.Text>{`${i.level} - ${i.team_lead.full_name}`}</Typography.Text>
                        <Typography.Caption>{`Expected date: ${dateFormatter(i.expected)}`}</Typography.Caption>
                        <Typography.Caption>{i.offices.map(o => o.name).join(', ')}</Typography.Caption>
                      </div>
                    </SelectAntd.Option>
                  ))}
                </Select>
                <Select
                  label="Office location"
                  placeholder="Select office location"
                  options={locationOptions}
                  disabled={isOnboarding || !getFieldValue('opening') || locationOptions.length < 2}
                  itemProps={{ name: 'office', rules: [REQUIRED_RULE] }}
                />
              </>
            )
          }
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(p, c) => p.decision !== c.decision}>
          {({ getFieldValue }) =>
            getFieldValue('decision') === JOB_SUBMIT_OPTIONS.REJECT && (
              <Select
                label={t('translation:popup_submit_offer_decision.label_rejection_reason')}
                placeholder={t('translation:popup_submit_offer_decision.ph_select_rejection_reason')}
                options={rejectionReasons}
                itemProps={{ name: 'rejection' }}
              />
            )
          }
        </Form.Item>
        <TextArea
          label={t('translation:popup_submit_offer_decision.label_comment')}
          maxLength={6000}
          placeholder={t('translation:popup_submit_offer_decision.ph_enter_comment')}
          itemProps={{ name: 'comment' }}
        />
      </Form>
    </Popup>
  );
};
